/**
 * CrewPairsTab — who flies with whom (database/344, Matt 2026-10-25). the Flight Board's Crews tab (before Pilots),
 * manager and admin seats only. A temporary home until the Flight Board is cleaned up.
 *
 * Pairs fly together; a person's first call is the kind of work that comes to them first; "flies solo
 * on" is work they take without a partner (Armando on permits). The Operations Director Triage's
 * "Fill the seats" cards read all three. People switched off as crew in Admin → Crew aren't listed.
 * Saves as you change it.
 */
import { useEffect, useState } from 'react'
import { getTeamMembers } from '../services/members.js'
import { getCrewRoster, setCrewPair } from '../services/flights.js'
import { alertDialog } from './Dialog.jsx'

const KINDS = [{ v: '', l: 'None' }, { v: 'permit', l: 'Permit work' }, { v: 'construction', l: 'Construction work' }]

export default function CrewPairsTab() {
  const [team, setTeam] = useState(null)
  const [roster, setRoster] = useState([])
  const [busy, setBusy] = useState('')
  useEffect(() => {
    let alive = true
    Promise.all([getTeamMembers(), getCrewRoster()])
      .then(([t, r]) => { if (alive) { setTeam(t || []); setRoster(r || []) } })
      .catch(e => { if (alive) { setTeam([]); alertDialog({ title: 'Could not load crews', message: e.message || '' }) } })
    return () => { alive = false }
  }, [])

  const byId = {}; for (const r of roster) byId[r.memberAirtableId] = r
  const crew = (team || []).filter(m => { const r = byId[m.id]; return !(r && (r.retiredAt || r.eligible === false)) })
    .sort((a, b) => String(a.name || '').localeCompare(String(b.name || '')))
  const nameOf = (id) => (crew.find(m => m.id === id) || {}).name || (byId[id] || {}).memberName || 'someone'

  async function save(m, patch) {
    setBusy(m.id)
    try {
      const extra = patch.partnerId ? { partnerName: nameOf(patch.partnerId) } : {}
      setRoster(await setCrewPair({ memberAirtableId: m.id, memberName: m.name, ...patch, ...extra }))
    } catch (e) { await alertDialog({ title: 'Could not save', message: e.message || 'The change did not save.' }) }
    finally { setBusy('') }
  }

  // pairs first (each once), then everyone flying without a set partner
  const seen = new Set(), pairs = [], singles = []
  for (const m of crew) {
    if (seen.has(m.id)) continue
    const p = byId[m.id] && byId[m.id].partnerId
    if (p && crew.some(x => x.id === p)) { pairs.push([m, crew.find(x => x.id === p)]); seen.add(m.id); seen.add(p) }
    else { singles.push(m); seen.add(m.id) }
  }

  // One compact card per pair (or person without one), laid out across the frame's whole width; the three
  // column names sit once at the top of each card instead of over every select (Matt, 2026-10-27).
  const row = (m) => {
    const r = byId[m.id] || {}
    return (
      <div key={m.id} style={{ ...S.row, opacity: busy === m.id ? 0.55 : 1 }}>
        <div style={S.name} title={m.name}>{m.name}</div>
        <select style={S.sel} aria-label={`${m.name} flies with`} value={r.partnerId || ''} disabled={!!busy} onChange={e => save(m, { partnerId: e.target.value || null })}>
          <option value="">No set partner</option>
          {crew.filter(x => x.id !== m.id).map(x => <option key={x.id} value={x.id}>{x.name}</option>)}
        </select>
        <select style={S.sel} aria-label={`${m.name} first call`} value={r.firstCall || ''} disabled={!!busy} onChange={e => save(m, { firstCall: e.target.value || null })}>
          {KINDS.map(k => <option key={k.v} value={k.v}>{k.l}</option>)}
        </select>
        <select style={S.sel} aria-label={`${m.name} flies solo on`} value={r.soloOn || ''} disabled={!!busy} onChange={e => save(m, { soloOn: e.target.value || null })}>
          {KINDS.map(k => <option key={k.v} value={k.v}>{k.l}</option>)}
        </select>
      </div>
    )
  }
  const card = (people) => (
    <div key={people[0].id} style={S.card}>
      <div style={{ ...S.row, ...S.colHead }}><span /><span>Flies with</span><span>First call</span><span>Flies solo on</span></div>
      {people.map(row)}
    </div>
  )

  return (
    <div style={S.wrap}>
      <div style={S.sub}>Pairs are kept together when new work is proposed. Saves as you change it.</div>
      {team === null && <div style={S.empty}>Loading the crew…</div>}
      <div style={S.grid}>
        {pairs.map(card)}
        {singles.map(m => card([m]))}
      </div>
    </div>
  )
}

const S = {
  wrap: { fontFamily: '"DM Sans", sans-serif' },
  sub: { fontSize: 12, color: 'var(--aj-text-muted)', marginBottom: 10 },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(460px, 1fr))', gap: 10 },
  card: { border: '0.5px solid var(--aj-border)', borderRadius: 10, background: 'var(--aj-bg-surface)', padding: '8px 12px', minWidth: 0 },
  row: { display: 'grid', gridTemplateColumns: 'minmax(96px, 1.1fr) repeat(3, minmax(0, 1fr))', alignItems: 'center', gap: 8, padding: '4px 0' },
  colHead: { fontSize: 10.5, color: 'var(--aj-text-muted)', padding: '0 0 2px' },
  name: { fontSize: 13, fontWeight: 600, color: 'var(--aj-text)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  sel: { fontFamily: '"DM Sans", sans-serif', fontSize: 12, color: 'var(--aj-text)', background: 'var(--aj-bg-subtle)', border: '0.5px solid var(--aj-border-mid)', borderRadius: 4, padding: '4px 6px', width: '100%', minWidth: 0 },
  empty: { padding: '16px 0', fontSize: 12, color: 'var(--aj-text-muted)' },
}
